import { onboardingRedirectEntryPoint } from '@jira/redirect-entrypoints__onboarding-redirect/src/onboardingRedirectEntryPoint.entrypoint.tsx';
import { MenuIdHome } from '@jira/navigation-apps__constants/src/MenuIdHome.tsx';
import { jiraHomeResourceWithCache } from '@jira/router-resources__jira-home-page-data/src/jiraHomeResourceWithCache.tsx';
import { jiraHomePageEntryPoint } from '@jira/spa-apps__jira-home/src/jiraHomePageEntryPoint.entrypoint.tsx';
import { CONSOLIDATION_STATE_RESOURCE_TYPE } from '@jira/router-resources__eoc-tenant-info/src/consolidationStateResourceType.constants.tsx';
import { getConsolidationStateResource } from '@jira/router-resources__eoc-tenant-info/src/getConsolidationStateResource.tsx';
import type { TenantInfo } from '@jira/router-resources__eoc-tenant-info/src/TenantInfo.types.tsx';
import { ForYouSkeleton } from '@jira/platform__skeletons/src/ui/for-you/ForYouSkeleton.tsx';
import { checkShouldUseJSM } from '@jira/eoc__tenant-info/src/controllers/utils.tsx';
import { homepageLayout } from '@jira/platform__layouts-homepage-layout/src/index.tsx';
import { createEntry } from '@jira/platform__routing-route-entry/src/createEntry.tsx';
import { getNavigationResources } from '@jira/router-resources__navigation/src/getNavigationResources.tsx';
import { onboardingFlowCompletedResource } from '@jira/router-resources__software-onboarding/src/onboardingFlowCompletedResource.tsx';
import { jiraHomeRoute } from '@jira/router-routes__jira-home-routes/src/jiraHomeRoute.tsx';
import { postOfficeJiraYourWorkResource } from '@jira/router-resources__post-office/src/conditionalPostOfficeJiraTopBannerResource.tsx';
import { postOfficeJiraForYouCrossflowSolutionsResource } from '@jira/router-resources__post-office/src/postOfficeJiraForYouCrossflowSolutionsResource.tsx';
import { resourceWithCondition } from '@jira/router-resources__utils/src/resourceWithCondition.tsx';
import { YourWorkSkeleton } from '@jira/platform__skeletons/src/ui/your-work/YourWorkItemListSkeleton.tsx';
// eslint-disable-next-line jira/import/file-extension-required
import { createPageEntryPoint } from '@jira/platform__routing-create-route-entry-point';
import { componentWithCondition } from '@jira/platform__utils-feature-flagging-utils/src/utils/component-with-condition/index.tsx';
import type {
	Route,
	RouteResourceResponse,
	RouteResources,
} from '@atlassian/react-resource-router';
import { fg } from '@atlassian/jira-feature-gating';
import { expVal } from '@atlassian/jira-feature-experiments';
import { widgetResources as alertsWidgetResources } from '@atlassian/eoc-alerts-widget/resources';
import { widgetResources as onCallWidgetResources } from '@atlassian/eoc-who-is-on-call/resources';

const conditionalPostOfficeJiraYourWorkResource = resourceWithCondition(
	() => fg('post_office_ssr_jira_your_work_enabled'),
	postOfficeJiraYourWorkResource,
);

const conditionalPostOfficeJiraForYouCrossflowSolutionsResource = resourceWithCondition(
	() => fg('jira-crossflow-solutions-for-you-killswitch'),
	postOfficeJiraForYouCrossflowSolutionsResource,
);

// Redefined here since it is not exported from RRR.
type ResourceDependencies = {
	[type: string]: RouteResourceResponse;
};

const checkConsolidationOnRouteDependency = async (dependencies: ResourceDependencies) => {
	const data = await dependencies[CONSOLIDATION_STATE_RESOURCE_TYPE].promise;
	// oxlint-disable-next-line typescript/consistent-type-assertions
	return checkShouldUseJSM(data as TenantInfo | null);
};

const jiraHomeEntryPoint = createPageEntryPoint({
	main: jiraHomePageEntryPoint,
	redirect: onboardingRedirectEntryPoint,
});

export const jiraHomeRouteEntry: Route = createEntry(jiraHomeRoute, () => ({
	group: 'home',
	ufoName: 'jira-home',
	layout: homepageLayout,
	skeleton: componentWithCondition(
		() => expVal('recommended_actions_on_for_you', 'isEnabled', false),
		ForYouSkeleton,
		YourWorkSkeleton,
	),

	entryPoint: () => jiraHomeEntryPoint,

	navigation: {
		menuId: MenuIdHome,
	},

	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	resources: [
		...getNavigationResources(),
		jiraHomeResourceWithCache,
		conditionalPostOfficeJiraYourWorkResource,
		getConsolidationStateResource(),
		...onCallWidgetResources.map((resourceCreator) =>
			resourceCreator(checkConsolidationOnRouteDependency, [CONSOLIDATION_STATE_RESOURCE_TYPE]),
		),
		...alertsWidgetResources.map((resourceCreator) =>
			resourceCreator(checkConsolidationOnRouteDependency, [CONSOLIDATION_STATE_RESOURCE_TYPE]),
		),

		// 10/12/2025: This REST resource is temporary and will be removed once we've added an equivalent GraphQL query
		// it will be used in UserSegmentationRedirect to determine if the user has completed the onboarding flow
		onboardingFlowCompletedResource,

		conditionalPostOfficeJiraForYouCrossflowSolutionsResource,
	] as RouteResources,

	supportsPreviewPanel: true,

	bifrost: {
		bifrostGroup: 'shell-only',
	},
	isStreamingEnabled: fg('jfp-magma-ssr-streaming-enable-for-you'),
}));
